import { useState } from "react";
import { Save, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { PictogramImage } from "./PictogramImage";
import { PictogramCategoryDropdown } from "./PictogramCategoryDropdown";

const BASE_COLOR = "#1A1A1A";

export const GlobalPictogramEditor = ({ draft, categories, busy, error, confirmOnOpen = false, onChange, onSave, onDelete, onClose, onReturnFocus }) => {
  const [confirm, setConfirm] = useState(confirmOnOpen);
  const set = (field, value) => onChange({ ...draft, [field]: value });
  const id = draft.id || "new";

  return <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
    <DialogContent showCloseButton={false} data-testid={`admin-picto-modal-${id}`}
      onCloseAutoFocus={event => { event.preventDefault(); onReturnFocus(); }}
      className="w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-sm bg-white p-5 sm:p-7">
      <DialogHeader className="text-left pr-7">
        <DialogTitle className="font-serif text-2xl">{draft.id ? "Modifica pittogramma" : "Nuovo pittogramma"}</DialogTitle>
        <DialogDescription className="sr-only">Nome, icona, colore e categoria del pittogramma condiviso.</DialogDescription>
      </DialogHeader>
      <button type="button" onClick={onClose} disabled={busy} aria-label="Chiudi" title="Chiudi"
        className="absolute right-5 top-5 text-[#525252] hover:text-[#1A1A1A]" data-testid="admin-picto-modal-close">
        <X className="w-5 h-5" />
      </button>
      <div data-testid="admin-picto-form" className="space-y-4 min-w-0">
        <div className="flex items-center gap-4 min-w-0 border-b border-[#E5E5E0] pb-4">
          <PictogramImage pictogram={{ ...draft, is_default: true, color_hex: draft.color_hex || (draft.preserve_original ? null : BASE_COLOR) }}
            className="w-16 h-16 shrink-0" testId={`admin-picto-preview-${id}`} />
          <div className="min-w-0">
            <span className="font-serif text-lg [overflow-wrap:anywhere]" data-testid={`admin-picto-preview-name-${id}`}>{draft.name || "Nuovo pittogramma"}</span>
            <p className="text-xs text-[#707070] mt-1" data-testid={`admin-picto-state-${id}`}>
              {draft.icon_file_id ? "Disponibile per tutti" : "Da completare · icona mancante"}
            </p>
          </div>
        </div>
        <div><label htmlFor="admin-picto-input-name" className="label-tiny">Nome</label>
          <input id="admin-picto-input-name" maxLength={80} value={draft.name} className="input-luxury w-full"
            onChange={e => set("name", e.target.value)} data-testid="admin-picto-input-name" /></div>
        <PictogramCategoryDropdown id="admin-picto-input-category" value={draft.category_id}
          options={categories} required onChange={value => set("category_id", value)}
          testId="admin-picto-input-category" />
        <div><label htmlFor="admin-picto-input-file" className="label-tiny">Icona SVG o PNG · max 2 MB</label>
          <input id="admin-picto-input-file" type="file" accept="image/svg+xml,image/png,.svg,.png"
            className="input-luxury w-full" disabled={busy} onChange={e => set("file", e.target.files?.[0] || null)}
            data-testid="admin-picto-input-file" />
          {draft.file && <p className="text-xs text-[#525252] mt-2" data-testid="admin-picto-filename">{draft.file.name}</p>}</div>
        <div><label htmlFor="admin-picto-input-color" className="label-tiny">Colore predefinito</label>
          <div className="flex items-center gap-3">
            <input id="admin-picto-input-color" type="color" value={draft.color_hex || BASE_COLOR}
              onChange={e => set("color_hex", e.target.value)} className="h-10 w-14 border border-[#D4D4D4] cursor-pointer"
              data-testid="admin-picto-input-color" />
            <span className="text-xs text-[#525252] font-mono" data-testid="admin-picto-color-value">
              {draft.color_hex ? draft.color_hex.toUpperCase() : draft.preserve_original ? "Colori originali" : BASE_COLOR}
            </span>
          </div>
          {draft.preserve_original && <button type="button" className="text-xs underline text-[#525252] mt-2"
            onClick={() => set("color_hex", draft.color_hex ? null : BASE_COLOR)} data-testid="admin-picto-original-toggle">
            {draft.color_hex ? "Usa colori originali" : "Applica colore selezionato"}
          </button>}
        </div>
        <div><label htmlFor="admin-picto-input-order" className="label-tiny">Ordine</label>
          <input id="admin-picto-input-order" type="number" value={draft.order_index} className="input-luxury w-full"
            onChange={e => set("order_index", e.target.value)} data-testid="admin-picto-input-order" /></div>
      </div>
      {error && <p role="alert" className="text-xs text-[#B91C1C]" data-testid="admin-picto-save-error">{error}</p>}
      {confirm && <div role="alertdialog" aria-label={`Elimina ${draft.name}`} className="min-w-0 max-w-full border-t pt-3 text-sm [overflow-wrap:anywhere]"
        data-testid={`admin-picto-confirm-${id}`}>
        <p className="min-w-0 w-full [overflow-wrap:anywhere]">Eliminare «{draft.name}»? Non è possibile se è usato in un prodotto.</p>
        <div className="flex gap-2 mt-3">
          <button type="button" disabled={busy} onClick={onDelete} className="btn-primary"
            data-testid={`admin-picto-confirm-delete-${id}`}>Elimina</button>
          <button type="button" disabled={busy} onClick={() => setConfirm(false)} className="btn-ghost"
            data-testid={`admin-picto-confirm-cancel-${id}`}>Annulla</button>
        </div>
      </div>}
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[#E5E5E0] pt-4">
        {draft.id && <button type="button" onClick={() => setConfirm(true)} disabled={busy}
          className="btn-ghost mr-auto inline-flex items-center gap-1 text-xs text-[#8C3A3A]"
          data-testid={`admin-picto-delete-${id}`}><Trash2 className="w-4 h-4" />Elimina</button>}
        <button type="button" className="btn-ghost" disabled={busy} onClick={onClose}
          data-testid="admin-picto-cancel">Annulla</button>
        <button type="button" className="btn-primary inline-flex items-center gap-2" disabled={busy} onClick={onSave}
          data-testid="admin-picto-save"><Save className="w-4 h-4" />{busy ? "Salvataggio..." : "Salva"}</button>
      </div>
    </DialogContent>
  </Dialog>;
};